import DashboardFiltration from '../../components/siteCompareDashboard/filtration';
import { Divider, LinearProgress, Box, Typography } from '@mui/material';
import ListView from '../../components/siteCompareDashboard/ListView';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { useState, useCallback, useRef, useEffect } from 'react';
import { isObjectEmpty } from '../../utils/isObjectEmpty';
import NoRecord from '../../components/NoRecord';
import axios from 'axios';
import '../../css/dashboard/dashboardApp.css';

export const SiteCompareDashboard = () => {

  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [showResponsiveFilter, setShowResponsiveFilter] = useState(false);

  const [filters, setFilters] = useState(
    JSON.parse(localStorage.getItem('siteCompareDashboardFilters')) || {
      debouncedSearch: '',
      selectedYear: new Date().getFullYear(),
    }
  );

  const defaultSortBy = {
    name: null,
    total_expenses_previous: null,
    purchase_price_previous: null,
    total_expenses_current: null,
    purchase_price_current: null,
  };

  const [sortBy, setSortBy] = useState(() => {
    const storedSortBy = JSON.parse(localStorage.getItem('siteCompareDashboardSortBy')) || {};
    return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
  });

  const [sites, setSites] = useState([]);
  const [sitesFetched, setSitesFetched] = useState(0);
  const [totalSites, setTotalSites] = useState(0);
  const [filteredSitesCount, setFilteredSitesCount] = useState(0);
  const [totalYearExpenses, setTotalYearExpenses] = useState(0);
  const [totalPreviousExpenses, setTotalPreviousExpenses] = useState(0);
  const [totalYearPrices, setTotalYearPrices] = useState(0);
  const [totalPreviousPrices, setTotalPreviousPrices] = useState(0);
  const [expenseComparePercentage, setExpenseComparePercentage] = useState({});
  const [priceComparePercentage, setPriceComparePercentage] = useState({});

  const [offset, setOffset] = useState(0);
  const limit = 30;

  const filtersEmpty = isObjectEmpty(filters, ['selectedYear']);

  useEffect(() => {
    setLoading(true)
  }, [filters, sortBy]);

  useEffect(() => {
    localStorage.setItem('siteCompareDashboardFilters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('siteCompareDashboardSortBy', JSON.stringify(sortBy));
  }, [sortBy]);

  // Scroll to top when filters change
  useEffect(() => {
    if (scrollRef.current) {
      setOffset(0);
      scrollRef.current.scrollTo(0);
    }
  }, [filters]);

  const fetchSites = useCallback(async () => {
    try {
      if (!filters?.selectedYear) return; // Ensure selectedYear is defined before making the request
      const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
      const { status, data } = await axios.get('site/dashboard/compare', { params: { offset: adjustedOffset, limit, filters, sortBy } });
      if (status === 200 || status === 201) {
        // Create a copy of the current files array
        const updatedSites = [];

        // Update the fetched records in their correct positions
        data?.sites?.forEach((file, index) => { updatedSites[adjustedOffset + index] = file });
        // Update the state with the new files array
        setLoading(false);
        setSites(updatedSites);
        setTotalSites(data?.totalSites);
        setFilteredSitesCount(data?.filteredSitesCount);
        setTotalYearExpenses(data?.totalExpensesSumCurrent);
        setTotalYearPrices(data?.totalPurchasePriceSumCurrent);
        setTotalPreviousExpenses(data?.totalExpensesSumPrevious);
        setTotalPreviousPrices(data?.totalPurchasePriceSumPrevious);
        setExpenseComparePercentage(data?.expenseComparePercentage);
        setPriceComparePercentage(data?.priceComparePercentage);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error Fetching sites in Site Compare Dashboard:', error);
      CustomErrorLogger(error, 'fetch sites in sites Compare dashboard page');
    }
  }, [offset, filters, sortBy]);

  useEffect(() => {
    fetchSites()
  }, [fetchSites]);

  return (
    <Box>
      {loading ?
        <Box>
          <DashboardFiltration
            filtersEmpty={filtersEmpty}
            filters={filters}
            setFilters={setFilters}
            totalSites={totalSites}
            filteredSitesCount={filteredSitesCount}
            sitesFetched={sitesFetched}
            showResponsiveFilter={showResponsiveFilter}
            setShowResponsiveFilter={setShowResponsiveFilter}
          />
          <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
        </Box> :
        filtersEmpty && sites?.length === 0 ?
          <NoRecord
            description="Aucun site ajouté pour le moment"
            buttonTitle="Ajouter un site"
          /> :
          <Box>
            <DashboardFiltration
              filtersEmpty={filtersEmpty}
              filters={filters}
              setFilters={setFilters}
              totalSites={totalSites}
              filteredSitesCount={filteredSitesCount}
              sitesFetched={sitesFetched}
              showResponsiveFilter={showResponsiveFilter}
              setShowResponsiveFilter={setShowResponsiveFilter}
            />

            <ListView
              scrollRef={scrollRef}
              filters={filters}
              sites={sites}
              sortBy={sortBy}
              setSortBy={setSortBy}
              setOffset={setOffset}
              filteredSitesCount={filteredSitesCount}
              setSitesFetched={setSitesFetched}
              showResponsiveFilter={showResponsiveFilter}
            />

            <Divider sx={{ marginTop: '20px', marginBottom: '20px' }} />
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Total dépenses {filters?.selectedYear}</Typography>
              <Typography className='addInvoice_total_typo'>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalYearExpenses)}</Typography>
            </Box>
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Total dépenses {filters?.selectedYear - 1}</Typography>
              <Typography className='addInvoice_total_typo'>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalPreviousExpenses)}</Typography>
            </Box>
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Dépenses évolution</Typography>
              <Typography className='addInvoice_total_typo' sx={{ color: expenseComparePercentage.pctColor, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: 12 }}>{expenseComparePercentage.arrow}</span>
                <span>{expenseComparePercentage.pctChangeDisplay}</span>
              </Typography>
            </Box>

            <Divider sx={{ marginTop: '20px', marginBottom: '20px' }} />
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Total prix d'achat {filters?.selectedYear}</Typography>
              <Typography className='addInvoice_total_typo'>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalYearPrices)}</Typography>
            </Box>
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Total prix d'achat {filters?.selectedYear - 1}</Typography>
              <Typography className='addInvoice_total_typo'>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalPreviousPrices)}</Typography>
            </Box>
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Prix d'achat évolution</Typography>
              <Typography className='addInvoice_total_typo' sx={{ color: priceComparePercentage.pctColor, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: 12 }}>{priceComparePercentage.arrow}</span>
                <span>{priceComparePercentage.pctChangeDisplay}</span>
              </Typography>
            </Box>
          </Box>
      }
    </Box>
  );
}